import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog"
import { Button } from "./ui/button"
import { Alert, AlertDescription } from "./ui/alert"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip"
import {
  X,
  Download,
  ZoomIn,
  ZoomOut,
  RotateCw,
  RotateCcw,
  Maximize,
  Minimize,
  Loader2,
  RefreshCw,
  ImageIcon
} from 'lucide-react';
import axios from 'axios';

const ImageViewer = ({ open, onClose, file }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scale, setScale] = useState(1.0);
  const [rotation, setRotation] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    if (open && file?.downloadUrl) {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');

      axios.get(file.downloadUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        responseType: 'blob'
      })
        .then(response => {
          const blob = new Blob([response.data], { type: response.headers['content-type'] || 'image/*' });
          const url = URL.createObjectURL(blob);
          setImageUrl(url);
          setLoading(false);
        })
        .catch(err => {
          console.error('Error fetching image:', err);
          const backendMsg = err.response && err.response.data && err.response.data.message;
          if (err.response && err.response.status === 404) {
            setError(backendMsg || 'File not found on the server (it may have been deleted from disk).');
          } else {
            setError('Failed to load image. Please check your connection and try again.');
          }
          setLoading(false);
        });
    }

    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, file?.downloadUrl]);

  const handleClose = () => {
    setScale(1.0);
    setRotation(0);
    setIsFullscreen(false);
    setLoading(true);
    setError(null);
    if (imageUrl) {
      URL.revokeObjectURL(imageUrl);
      setImageUrl(null);
    }
    onClose();
  };

  const handleZoomIn = () => setScale((prev) => Math.min(prev + 0.25, 5.0));
  const handleZoomOut = () => setScale((prev) => Math.max(prev - 0.25, 0.25));
  const handleReset = () => { setScale(1.0); setRotation(0); };
  const handleRotateLeft = () => setRotation((prev) => prev - 90);
  const handleRotateRight = () => setRotation((prev) => prev + 90);
  const toggleFullscreen = () => setIsFullscreen(!isFullscreen);

  const handleDownload = () => {
    if (imageUrl && file) {
      const link = document.createElement('a');
      link.href = imageUrl;
      link.download = file.name || 'image';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(val) => !val && handleClose()}>
      <DialogContent className={`flex flex-col p-0 gap-0 border-none ${isFullscreen ? 'w-screen h-screen max-w-none rounded-none' : 'w-[90vw] h-[90vh] max-w-7xl'}`}>

        {/* Header */}
        <div className="flex items-center justify-between border-b px-4 py-3 bg-white z-10">
          <div className="flex items-center gap-2 overflow-hidden">
            <ImageIcon className="h-5 w-5 text-emerald-500 shrink-0" />
            <h3 className="font-semibold text-gray-900 truncate max-w-md">
              {file?.name || 'Image Viewer'}
            </h3>
          </div>

          <div className="flex items-center gap-1">
            <div className="items-center gap-1 bg-gray-100 rounded-md p-0.5 hidden sm:flex">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon" onClick={handleZoomOut} disabled={loading} className="h-8 w-8 text-gray-700">
                      <ZoomOut className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Zoom Out</TooltipContent>
                </Tooltip>
              </TooltipProvider>

              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon" onClick={handleZoomIn} disabled={loading} className="h-8 w-8 text-gray-700">
                      <ZoomIn className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Zoom In</TooltipContent>
                </Tooltip>
              </TooltipProvider>

              <div className="w-px h-4 bg-gray-300 mx-1" />

              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon" onClick={handleRotateLeft} disabled={loading} className="h-8 w-8 text-gray-700">
                      <RotateCcw className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Rotate Left</TooltipContent>
                </Tooltip>
              </TooltipProvider>

              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon" onClick={handleRotateRight} disabled={loading} className="h-8 w-8 text-gray-700">
                      <RotateCw className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Rotate Right</TooltipContent>
                </Tooltip>
              </TooltipProvider>

              <div className="w-px h-4 bg-gray-300 mx-1" />

              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon" onClick={handleReset} disabled={loading} className="h-8 w-8 text-gray-700">
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Reset Transform</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>

            <div className="flex items-center gap-1 ml-2">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon" onClick={handleDownload} disabled={loading} className="h-9 w-9 text-gray-700">
                      <Download className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Download</TooltipContent>
                </Tooltip>
              </TooltipProvider>

              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon" onClick={toggleFullscreen} className="h-9 w-9 text-gray-700">
                      {isFullscreen ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}</TooltipContent>
                </Tooltip>
              </TooltipProvider>

              <Button variant="ghost" size="icon" onClick={handleClose} className="h-9 w-9 hover:bg-gray-200 rounded-full text-gray-500">
                <X className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 bg-gray-100 overflow-hidden relative flex items-center justify-center p-4">
          {loading && (
            <div className="flex flex-col items-center gap-2 text-gray-500">
              <Loader2 className="h-10 w-10 animate-spin text-primary" />
              <p className="font-medium">Loading image...</p>
            </div>
          )}

          {error && (
            <div className="w-full max-w-md">
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            </div>
          )}

          {imageUrl && !loading && (
            <div className="w-full h-full flex items-center justify-center overflow-auto rounded-lg">
              <img
                src={imageUrl}
                alt={file?.name || 'Viewed Image'}
                className="max-w-full max-h-full object-contain transition-transform duration-200 ease-out"
                style={{
                  transform: `scale(${scale}) rotate(${rotation}deg)`,
                  cursor: scale > 1 ? 'move' : 'default'
                }}
              />
            </div>
          )}

          {/* Footer Overlay Info */}
          {!loading && !error && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 text-white rounded-full px-4 py-1.5 flex items-center gap-3 text-sm backdrop-blur-sm shadow-lg">
              <span>{Math.round(scale * 100)}%</span>
              {rotation !== 0 && (
                <>
                  <span className="w-px h-3 bg-white/30" />
                  <span>{rotation}°</span>
                </>
              )}
            </div>
          )}
        </div>

      </DialogContent>
    </Dialog>
  );
};

export default ImageViewer;
